/**
 * Coordinates overlay trace fetches with generation tokens to ignore stale responses.
 * Use createOverlayFetchCoordinator() to get a per-instance coordinator so that
 * multiple simultaneous ChartsView/TagChart instances do not share generation
 * state (a shared singleton lets one chart cancel another chart's fetches).
 */

export const buildOverlayFetchKey = ({
  tagId,
  timestamp,
  ampType,
  traceKey,
  type,
  feature,
  configId
}) => {
  let ts;
  if (timestamp instanceof Date) {
    ts = timestamp.toISOString();
  } else if (timestamp !== null && typeof timestamp === 'object' && typeof timestamp.valueOf === 'function') {
    // Avoid "[object Object]" collapsing distinct points if a non-Date,
    // value-bearing object (e.g. a moment) ever reaches here.
    ts = String(timestamp.valueOf());
  } else {
    ts = String(timestamp);
  }
  return `${tagId}|${configId || ''}|${ts}|${ampType || ''}|${type || ''}|${feature || ''}|${traceKey}`;
};

export const createOverlayFetchCoordinator = () => {
  const inFlightKeys = new Map();
  let generation = 0;

  const beginOverlayFetch = (fetchKey) => {
    generation += 1;
    inFlightKeys.set(fetchKey, generation);
    return generation;
  };

  const isOverlayFetchCurrent = (fetchKey, gen) =>
    inFlightKeys.get(fetchKey) === gen;

  const endOverlayFetch = (fetchKey, gen) => {
    if (inFlightKeys.get(fetchKey) === gen) {
      inFlightKeys.delete(fetchKey);
    }
  };

  const clearAllOverlayFetches = () => {
    inFlightKeys.clear();
  };

  return {
    beginOverlayFetch,
    isOverlayFetchCurrent,
    endOverlayFetch,
    clearAllOverlayFetches
  };
};

/**
 * Run async tasks with bounded concurrency.
 * @param {Array} items
 * @param {number} limit
 * @param {Function} worker - async (item, index) => void
 */
export const runWithConcurrencyLimit = async (items, limit, worker) => {
  if (!items || items.length === 0) return;
  const concurrency = Math.max(1, limit || 6);
  let index = 0;

  const runNext = async () => {
    while (index < items.length) {
      const currentIndex = index;
      index += 1;
      // Guard each worker so a single failure (sync throw or rejection) cannot
      // abort the remaining queued items or surface as an unhandled rejection.
      // Individual workers own their own point-level 'error' status.
      try {
        await worker(items[currentIndex], currentIndex);
      } catch (error) {
        console.warn('Overlay fetch worker failed:', error);
      }
    }
  };

  const runners = [];
  for (let i = 0; i < Math.min(concurrency, items.length); i += 1) {
    runners.push(runNext());
  }
  await Promise.all(runners);
};
